import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, Loader, MapPin, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FacilityCheckinPage() {
  const { facilityId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | done | error | need_login
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setStatus("need_login"); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/facility/checkin`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          credentials: "include", body: JSON.stringify({ facility_id: facilityId }),
        });
        if (r.ok) { setResult(await r.json()); setStatus("done"); }
        else setStatus("error");
      } catch { setStatus("error"); }
    })();
  }, [user, authLoading, facilityId]);

  const returnTo = encodeURIComponent(`/facility/checkin/${facilityId}`);

  return (
    <div className="min-h-screen bg-[#0F1629] flex items-center justify-center px-4 dark-section" data-testid="facility-checkin-page">
      <div className="max-w-md w-full text-center">
        {(status === "loading" || authLoading) && (
          <>
            <Loader size={40} className="text-brand-green animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Checking you in…</h1>
            <p className="text-gray-400 text-sm">One moment.</p>
          </>
        )}

        {status === "need_login" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-brand-green/10 border border-brand-green/30 flex items-center justify-center mx-auto mb-5">
              <LogIn size={28} className="text-brand-green" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Log in to check in</h1>
            <p className="text-gray-400 text-sm mb-6">You need to be signed in to check in to this venue.</p>
            <Link to={`/login?returnTo=${returnTo}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors"
              data-testid="checkin-login-btn">
              <LogIn size={16} /> Sign In
            </Link>
          </>
        )}

        {status === "done" && (
          <>
            <div className="w-20 h-20 rounded-full bg-brand-green/10 border border-brand-green/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={36} className="text-brand-green" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-heading font-black text-white mb-3" data-testid="checkin-success-title">
              {result?.already_checked_in ? "Already checked in" : "You're checked in!"}
            </h1>
            <p className="text-gray-300 mb-1 flex items-center justify-center gap-1.5">
              <MapPin size={15} className="text-brand-green" /> {result?.facility_name}
            </p>
            {result?.session_title && <p className="text-gray-400 text-sm mb-8">Session: {result.session_title}</p>}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link to="/group-sessions" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors">
                Browse Sessions
              </Link>
              <Link to="/find-partners" className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-white font-medium rounded-xl hover:border-white/25 transition-colors">
                Find Partners
              </Link>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-red-400/10 border border-red-400/20 flex items-center justify-center mx-auto mb-5">
              <AlertCircle size={28} className="text-red-400" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Check-in failed</h1>
            <p className="text-gray-400 text-sm mb-6">We couldn't find that venue. Please scan the QR code again.</p>
            <Link to="/" className="text-brand-green hover:underline text-sm">Back to Home</Link>
          </>
        )}
      </div>
    </div>
  );
}
